/**
 * StickyPlayer — mobile-only sticky-top wrapper that hosts whichever camera
 * is currently being watched. Hidden on desktop (the desktop shell renders
 * the player inline via the MainPlayer primitive in the `player` slot).
 *
 * Visibility is controlled entirely by the shell CSS (`.lv-sticky-player`
 * is `display: none` above 1100px and reappears with sticky positioning
 * below).
 */

import { AnimatePresence, motion } from 'framer-motion'
import {
  registerPrimitive,
  type PrimitiveProps,
} from '../../layout'
import {
  CameraVideo,
  useCameraSelection,
  useCameras,
  useViewerCount,
} from '../../destination'
import { EmptyStateInner } from './EmptyState'

interface StickyPlayerConfig {
  showWatchingCount?: boolean
}

export function StickyPlayer({ config }: PrimitiveProps) {
  const cameras = useCameras()
  const selection = useCameraSelection()
  const viewerCount = useViewerCount()
  const cfg = config as StickyPlayerConfig
  const showWatchingCount = cfg.showWatchingCount !== false

  const camera = cameras.find(
    (candidate) => candidate.identity === selection.selectedIdentity,
  )
  const watching = viewerCount ?? 0
  const liveCount = cameras.filter((c) => c.online).length

  return (
    <div className="lv-sticky-player">
      <AnimatePresence mode="sync" initial={false}>
        {camera ? (
          <motion.div
            key={camera.identity}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ position: 'absolute', inset: 0 }}
          >
            <CameraVideo
              identity={camera.identity}
              className="lv-player-feed"
              previewClassName="lv-player-feed"
              alt={camera.name}
              fallback={<EmptyStateInner state="awaiting-cameras" />}
            />
          </motion.div>
        ) : (
          <EmptyStateInner state={liveCount === 0 ? 'awaiting-cameras' : 'pre-event'} />
        )}
      </AnimatePresence>

      <div className="lv-player-vignette" />

      {/* Live only while the shown camera is online, never over the empty state. */}
      {camera?.online && (
        <span className="lv-player-live-chip" role="img" aria-label="Live">
          <span className="lv-live-dot" />
        </span>
      )}

      {showWatchingCount && watching > 0 && (
        <span className="lv-player-watching" role="img" aria-label={`${watching} watching live`}>
          <span className="lv-live-dot" />
          <b>{watching.toLocaleString()}</b>
        </span>
      )}

      {camera && (
        <div className="lv-player-name-overlay">
          <h3 className="lv-player-name">{camera.name}</h3>
        </div>
      )}
    </div>
  )
}

registerPrimitive('StickyPlayer', StickyPlayer)
